import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, CheckCircle, AlertCircle, X } from 'lucide-react';
import { assessmentAPI } from '../../services/api';

const FileUpload = ({ onUploadSuccess, onUploadError }) => {
  const [uploadState, setUploadState] = useState({
    status: 'idle', // idle, uploading, success, error
    progress: 0,
    file: null,
    error: null
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) {
      console.log('📁 No file selected');
      return;
    }

    console.log('📁 File Upload Started:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      lastModified: new Date(file.lastModified).toISOString()
    });

    setUploadState({
      status: 'uploading',
      progress: 0,
      file,
      error: null
    });

    try {
      console.log('🔗 Step 1: Generating upload URL...');
      
      // Generate upload URL
      const uploadData = await assessmentAPI.generateUploadUrl(file.name, file.type);
      
      console.log('✅ Step 1 Complete: Upload URL generated:', {
        uploadUrl: uploadData.upload_url,
        fileKey: uploadData.file_key,
        expiresIn: uploadData.expires_in
      });
      
      console.log('📤 Step 2: Uploading file to S3...');
      
      // Upload file to S3
      await assessmentAPI.uploadFile(uploadData.upload_url, file);
      
      console.log('✅ Step 2 Complete: File uploaded to S3 successfully');
      
      setUploadState(prev => ({
        ...prev,
        status: 'success',
        progress: 100
      }));

      console.log('🎉 Upload Process Complete!');

      // Notify parent component
      if (onUploadSuccess) {
        const successData = {
          filename: file.name,
          s3Key: uploadData.file_key,
          size: file.size
        };
        console.log('📢 Notifying parent component:', successData);
        onUploadSuccess(successData);
      }

    } catch (error) {
      console.error('❌ Upload Process Failed:', {
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      // Log additional error details
      if (error.response) {
        console.error('📡 Server Response Error:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        console.error('📡 Network Request Error:', {
          request: error.request,
          message: 'No response received from server'
        });
      }
      
      setUploadState(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Upload failed'
      }));

      if (onUploadError) {
        onUploadError(error);
      }
    }
  }, [onUploadSuccess, onUploadError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: uploadState.status === 'uploading'
  });

  const resetUpload = () => {
    setUploadState({
      status: 'idle',
      progress: 0,
      file: null,
      error: null
    });
  };

  const renderUploadArea = () => {
    if (uploadState.status === 'success') {
      return (
        <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
          <CheckCircle size={64} style={{ margin: '0 auto 1.5rem auto', color: '#10b981' }} />
          <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1rem' }}>
            Upload Successful!
          </h3>
          <p style={{ fontSize: '1rem', color: '#374151', marginBottom: '2rem', lineHeight: '1.5' }}>
            <strong>{uploadState.file?.name}</strong> has been uploaded successfully and is being processed.
          </p>
          <button 
            onClick={resetUpload}
            style={{
              backgroundColor: '#6366f1',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#5b21b6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#6366f1'}
          >
            Upload Another File
          </button>
        </div>
      );
    }

    if (uploadState.status === 'error') {
      return (
        <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
          <AlertCircle size={64} style={{ margin: '0 auto 1.5rem auto', color: '#ef4444' }} />
          <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1rem' }}>
            Upload Failed
          </h3>
          <p style={{ fontSize: '1rem', color: '#ef4444', marginBottom: '2rem', lineHeight: '1.5' }}>
            {uploadState.error}
          </p>
          <button 
            onClick={resetUpload}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
          >
            Try Again
          </button>
        </div>
      );
    }

    if (uploadState.status === 'uploading') {
      return (
        <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
          <div style={{ margin: '0 auto 1.5rem auto', width: '64px', height: '64px' }}>
            <svg 
              style={{ animation: 'spin 1s linear infinite', width: '64px', height: '64px', color: '#6366f1' }} 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <circle 
                style={{ opacity: 0.25 }} 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
              />
              <path 
                style={{ opacity: 0.75 }} 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1rem' }}>
            Uploading...
          </h3>
          <p style={{ fontSize: '1rem', color: '#374151', marginBottom: '2rem' }}>
            <strong>{uploadState.file?.name}</strong>
          </p>
          <div style={{ 
            width: '100%', 
            backgroundColor: '#f3f4f6', 
            borderRadius: '12px', 
            height: '12px',
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div 
              style={{ 
                background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                height: '12px', 
                borderRadius: '12px', 
                transition: 'width 0.3s ease',
                width: `${uploadState.progress}%`
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div style={{
        textAlign: 'center',
        padding: '4rem 2rem',
        border: '3px dashed #cbd5e1',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        backgroundColor: isDragActive ? '#f8fafc' : '#fafbfc',
        borderColor: isDragActive ? '#6366f1' : '#cbd5e1',
        cursor: 'pointer'
      }}>
        <Upload size={64} style={{ margin: '0 auto 1.5rem auto', color: '#6366f1' }} />
        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1rem' }}>
          {isDragActive ? 'Drop your Excel file here' : 'Upload GPS Assessment File'}
        </h3>
        <p style={{ fontSize: '1rem', color: '#374151', marginBottom: '0.5rem', lineHeight: '1.5' }}>
          Drag and drop your Excel file here, or click to browse
        </p>
        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Supports .xlsx and .xls files
        </p>
      </div>
    );
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '48rem',
      margin: '0 auto',
      backgroundColor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '16px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '2rem 2rem 1rem 2rem',
        backgroundColor: '#fafbfc',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#111827',
          margin: 0,
          textAlign: 'center'
        }}>
          Upload Assessment Data
        </h2>
        <p style={{
          fontSize: '1rem',
          color: '#6b7280',
          margin: '0.5rem 0 0 0',
          textAlign: 'center'
        }}>
          Upload your Excel file to generate questionnaires
        </p>
      </div>
      
      {/* Upload Area */}
      <div style={{ padding: '2rem' }}>
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          {renderUploadArea()}
        </div>

        {/* File Info Footer */}
        {uploadState.file && uploadState.status !== 'success' && uploadState.status !== 'error' && (
          <div style={{
            marginTop: '2rem',
            padding: '1rem 1.5rem',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <File size={20} style={{ color: '#6366f1' }} />
              <span style={{ fontSize: '1rem', color: '#374151', fontWeight: '500' }}>
                {uploadState.file.name}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                resetUpload();
              }}
              style={{
                backgroundColor: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                cursor: 'pointer',
                padding: '0.5rem',
                color: '#64748b',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e2e8f0';
                e.target.style.color = '#475569';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f1f5f9';
                e.target.style.color = '#64748b';
              }}
            >
              <X size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;